import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { ModalComponent } from '@catch/general-ui';
import { TranslocoPipe } from '@jsverse/transloco';
import { PlatformApplyChoiceKind } from '../../enums/platform-apply-choice-kind.enum';
import { PlatformMissingDataType } from '../../enums/platform-missing-data-type.enum';
import { PlatformApplication } from '../../models/platform.models';
import {
  PlatformApplyWithProfileContext,
  PlatformJobsService,
} from '../../services/platform-jobs.service';
import { PlatformApplyWithProfileService } from '../../services/platform-apply-with-profile.service';

const PROFILE_FOLLOW_UP_TYPES: ReadonlySet<PlatformMissingDataType> = new Set([
  PlatformMissingDataType.SALARY,
  PlatformMissingDataType.START_DATE,
  PlatformMissingDataType.DOCUMENTS,
  PlatformMissingDataType.SKILLS,
]);

@Component({
  selector: 'app-platform-follow-up-choice-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ModalComponent, TranslocoPipe],
  templateUrl: './platform-follow-up-choice-modal.component.html',
  styleUrl: './platform-follow-up-choice-modal.component.scss',
})
export class PlatformFollowUpChoiceModalComponent {
  private readonly applyWithProfile = inject(PlatformApplyWithProfileService);
  private readonly jobs = inject(PlatformJobsService);
  private readonly router = inject(Router);
  private readonly modal = viewChild.required<ModalComponent>('modal');

  readonly chooseManual = output<PlatformApplication>();

  readonly kind = signal(PlatformApplyChoiceKind.FOLLOW_UP);
  readonly application = signal<PlatformApplication | null>(null);
  readonly applyContext = signal<PlatformApplyWithProfileContext | null>(null);

  readonly titleKey = computed(() =>
    this.kind() === PlatformApplyChoiceKind.APPLY
      ? 'jobboard.platform.apply-with-profile.choice.apply-title'
      : 'jobboard.platform.apply-with-profile.choice.title'
  );

  readonly leadKey = computed(() =>
    this.kind() === PlatformApplyChoiceKind.APPLY
      ? 'jobboard.platform.apply-with-profile.choice.apply-lead'
      : 'jobboard.platform.apply-with-profile.choice.lead'
  );

  readonly profileLabelKey = computed(() =>
    this.kind() === PlatformApplyChoiceKind.APPLY
      ? 'jobboard.platform.apply-with-profile.choice.apply-profile'
      : 'jobboard.platform.apply-with-profile.choice.profile'
  );

  readonly manualLabelKey = computed(() =>
    this.kind() === PlatformApplyChoiceKind.APPLY
      ? 'jobboard.platform.apply-with-profile.choice.apply-manual'
      : 'jobboard.platform.apply-with-profile.choice.manual'
  );

  readonly manualHintKey = computed(() =>
    this.kind() === PlatformApplyChoiceKind.APPLY
      ? 'jobboard.platform.apply-with-profile.choice.apply-manual-hint'
      : 'jobboard.platform.apply-with-profile.choice.manual-hint'
  );

  readonly profileAvailable = computed(() => {
    if (this.kind() === PlatformApplyChoiceKind.APPLY) {
      return true;
    }
    return this.canFollowUpWithProfile(this.application()?.missingData ?? []);
  });

  readonly manualAvailable = computed(() => {
    if (this.kind() !== PlatformApplyChoiceKind.APPLY) {
      return true;
    }
    return this.applyContext()?.manualApplyUrl != null;
  });

  readonly profileHintKey = computed(() => {
    if (this.kind() === PlatformApplyChoiceKind.APPLY) {
      return 'jobboard.platform.apply-with-profile.choice.apply-profile-hint';
    }
    const missing = this.application()?.missingData ?? [];
    if (!this.canFollowUpWithProfile(missing)) {
      return 'jobboard.platform.apply-with-profile.choice.profile-unavailable';
    }
    if (missing.includes(PlatformMissingDataType.ISA)) {
      return 'jobboard.platform.apply-with-profile.choice.profile-partial-isa';
    }
    return 'jobboard.platform.apply-with-profile.choice.profile-hint';
  });

  /**
   * Opens the choice modal for an incomplete application.
   * @param application - Application that still needs missing data
   */
  open(application: PlatformApplication): void {
    this.kind.set(PlatformApplyChoiceKind.FOLLOW_UP);
    this.applyContext.set(null);
    this.application.set(application);
    this.modal().open();
  }

  /**
   * Opens the choice modal for applying to a job.
   * @param context - Apply-with-profile context
   */
  openApply(context: PlatformApplyWithProfileContext): void {
    this.kind.set(PlatformApplyChoiceKind.APPLY);
    this.application.set(null);
    this.applyContext.set(context);
    this.modal().open();
  }

  /**
   * Clears pending state when the modal is dismissed.
   */
  onDismissed(): void {
    this.application.set(null);
    this.applyContext.set(null);
  }

  /**
   * Continues with the profile-based flow.
   */
  chooseProfile(): void {
    if (!this.profileAvailable()) {
      return;
    }
    if (this.kind() === PlatformApplyChoiceKind.APPLY) {
      const context = this.applyContext();
      if (!context) {
        return;
      }
      this.modal().close();
      this.applyWithProfile.open(context);
      this.applyContext.set(null);
      return;
    }
    const application = this.application();
    if (!application) {
      return;
    }
    this.modal().close();
    this.applyWithProfile.open(
      this.jobs.followUpContextFromApplication(application)
    );
    this.application.set(null);
  }

  /**
   * Continues with the manual form.
   */
  chooseManualOption(): void {
    if (!this.manualAvailable()) {
      return;
    }
    if (this.kind() === PlatformApplyChoiceKind.APPLY) {
      const url = this.applyContext()?.manualApplyUrl;
      if (!url) {
        return;
      }
      this.modal().close();
      this.applyContext.set(null);
      void this.router.navigateByUrl(url);
      return;
    }
    const application = this.application();
    if (!application) {
      return;
    }
    this.modal().close();
    this.chooseManual.emit(application);
    this.application.set(null);
  }

  /**
   * Whether any missing field can be filled via the profile flow.
   * @param missing - Missing data types
   * @returns True when profile follow-up is useful
   */
  private canFollowUpWithProfile(missing: PlatformMissingDataType[]): boolean {
    return missing.some((type) => PROFILE_FOLLOW_UP_TYPES.has(type));
  }
}
